import React from "react";
import {
  CheckCircle2,
  ShieldAlert,
  Search,
  Lock,
  Inbox,
  Info,
  LucideIcon,
} from "lucide-react";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { Button } from "../ui/Button";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export type StatusVariant =
  | "success"
  | "error"
  | "not_found"
  | "restricted"
  | "empty"
  | "info";

export interface StatusAction {
  label: string;
  onClick: () => void;
  icon?: LucideIcon;
  variant?: "primary" | "secondary";
}

export interface StatusScreenProps {
  /** The status variant defining the visual theme and icon */
  variant: StatusVariant;
  /** Primary headline */
  title: string;
  /** Secondary sub-headline (Upper-case tag) */
  message: React.ReactNode;
  /** Detailed contextual text */
  description?: React.ReactNode;
  /** Action buttons */
  actions?: StatusAction[];
  /** Forensic trace/code block content */
  code?: string;
  /** Whether to occupy full screen or behave as a card. Defaults to true. */
  fullScreen?: boolean;
  /** Optional override for the primary interactive color */
  primaryColor?: string;
  /** Optional compact mode for smaller panel embeds */
  compact?: boolean;
  /** Optional styling overrides from the parent layout */
  className?: string;
}

const variantConfig: Record<
  StatusVariant,
  { icon: LucideIcon; colorClass: string; bgClass: string }
> = {
  success: {
    icon: CheckCircle2,
    colorClass: "text-emerald-600",
    bgClass: "bg-emerald-50/50 border border-emerald-100/50",
  },
  error: {
    icon: ShieldAlert,
    colorClass: "text-red-600",
    bgClass: "bg-red-50/50 border border-red-100/50",
  },
  not_found: {
    icon: Search,
    colorClass: "text-slate-600",
    bgClass: "bg-slate-50/50 border border-slate-100",
  },
  restricted: {
    icon: Lock,
    colorClass: "text-amber-600",
    bgClass: "bg-amber-50/50 border border-amber-100/50",
  },
  empty: {
    icon: Inbox,
    colorClass: "text-slate-400",
    bgClass: "bg-slate-50/30 border border-slate-100/80",
  },
  info: {
    icon: Info,
    colorClass: "text-blue-600",
    bgClass: "bg-blue-50/50 border border-blue-100/50",
  },
};

/**
 * StatusScreen (V3.1.0)
 * Purpose: Unified, premium UI singleton for status reporting.
 * Aesthetic: Professional, restrained typography and micro-spacing for embedded blocks.
 */
export const StatusScreen: React.FC<StatusScreenProps> = ({
  variant,
  title,
  message,
  description,
  actions = [],
  code,
  fullScreen = true,
  primaryColor,
  compact = false,
  className,
}) => {
  const config = variantConfig[variant];
  const Icon = config.icon;

  return (
    <div
      className={cn(
        "flex flex-col select-none transition-all duration-500",
        fullScreen
          ? "min-h-[100dvh] w-full items-start justify-center bg-[#FAF9F5] p-6 sm:p-16 md:p-24"
          : compact
            ? "w-full py-5 px-3 rounded-[10px] border border-slate-100/80 bg-white"
            : "w-full py-8 px-4 rounded-[10px] border border-slate-100 bg-white",
        className,
      )}
    >
      <div
        className={cn(
          "w-full transition-all duration-700 animate-in fade-in slide-in-from-bottom-4",
          fullScreen
            ? "max-w-2xl text-left flex flex-col items-start"
            : "max-w-md mx-auto text-center flex flex-col items-center",
          compact && "max-w-sm",
        )}
      >
        {/* Refined Small Shield */}
        <div
          className={cn(
            "inline-flex items-center justify-center transition-transform hover:scale-105 active:scale-95 duration-300",
            compact ? "mb-3 p-1.5 rounded-[8px]" : "mb-4 p-2 rounded-[10px]",
            primaryColor
              ? "bg-slate-50 border border-slate-200/60"
              : config.bgClass,
          )}
          style={
            primaryColor
              ? {
                  backgroundColor: `${primaryColor}10`,
                  borderColor: `${primaryColor}25`,
                }
              : undefined
          }
        >
          <Icon
            size={compact ? 16 : 20}
            strokeWidth={1.5}
            className={cn(
              "w-auto h-auto transition-transform duration-300",
              config.colorClass,
            )}
            style={primaryColor ? { color: primaryColor } : undefined}
          />
        </div>

        {/* Typography Area */}
        <div
          className={cn(
            "space-y-2",
            compact ? "mb-4" : "mb-6",
            fullScreen
              ? "text-left flex flex-col items-start"
              : "text-center flex flex-col items-center",
          )}
        >
          <p
            className={cn(
              "font-medium uppercase tracking-[0.2em] text-slate-400",
              compact
                ? "text-[8px] sm:text-[9px]"
                : "text-[10px] sm:text-[11px]",
            )}
          >
            {message}
          </p>
          <h1
            className={cn(
              "text-slate-900 leading-[1.15] font-sans-custom font-bold tracking-tight",
              fullScreen
                ? "text-3xl sm:text-4xl lg:text-5xl font-black"
                : compact
                  ? "text-sm font-semibold"
                  : "text-base sm:text-lg",
            )}
          >
            {title}
          </h1>
          {description && (
            <p
              className={cn(
                "font-normal text-slate-500 leading-relaxed pt-1",
                fullScreen
                  ? "text-sm sm:text-base max-w-xl font-light"
                  : compact
                    ? "text-[11px] max-w-[260px]"
                    : "text-xs sm:text-sm max-w-[320px]",
              )}
            >
              {description}
            </p>
          )}
        </div>

        {/* Diagnostic Technical Console */}
        {code && (
          <div className={cn("w-full text-left", compact ? "mb-4" : "mb-6")}>
            <details
              className="cursor-pointer group select-none"
              id="sentry-error-details"
            >
              <summary className="text-[10px] font-bold text-slate-400 uppercase tracking-widest list-none flex items-center gap-2 hover:text-slate-600 transition-colors [&::-webkit-details-marker]:hidden">
                <span
                  className={cn(
                    "w-1.5 h-1.5 rounded-full animate-pulse",
                    config.colorClass.replace("text-", "bg-"),
                  )}
                />
                <span>Technical Logs</span>
              </summary>
              <div
                className={cn(
                  "bg-slate-50 border border-slate-100 text-left transition-colors group-hover:bg-slate-100/50 duration-300 mt-2",
                  compact ? "p-2 rounded-[8px]" : "p-3.5 rounded-[10px]",
                )}
              >
                <code
                  className={cn(
                    "text-slate-600 block whitespace-pre-wrap break-words font-mono font-medium leading-relaxed",
                    compact ? "text-[10px]" : "text-[11px]",
                  )}
                >
                  {code}
                </code>
              </div>
            </details>
          </div>
        )}

        {/* Action Bridge */}
        {actions.length > 0 && (
          <div
            className={cn(
              "flex flex-wrap gap-2 w-full",
              fullScreen ? "justify-start mt-2" : "justify-center mt-2",
            )}
          >
            {actions.map((action, idx) => {
              const ActionIcon = action.icon;
              const isSecondary = action.variant === "secondary";

              return (
                <Button
                  key={`${action.label}-${idx}`}
                  onClick={action.onClick}
                  variant={isSecondary ? "outline" : "primary"}
                  size={compact ? "sm" : "md"}
                  primaryColor={primaryColor}
                  className="w-full sm:w-auto"
                  leftIcon={
                    ActionIcon ? (
                      <ActionIcon size={compact ? 12 : 14} />
                    ) : undefined
                  }
                >
                  {action.label}
                </Button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
